import React, { useRef, useContext } from "react"
import useStyles from "./styles"
import {
  Input,
  AppBar,
  Toolbar,
  Button,
  Box,
  useMediaQuery,
} from "@material-ui/core"
import { useTheme } from "@material-ui/core/styles"
import SearchIcon from "@material-ui/icons/Search"
import moment from "moment"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import { openSnackBar } from "../../../../../actions/snack-bar"

import SuperAdminDatePicker from "../SuperAdminDatePicker/index"
import SuperTableContext from "../../../../../pages/Billing/SuperTableContext"
import AutocompleteBilling from "../../../Common/AutocompleteBilling/index"
import ExportCsvButton from "../ExportCsvButton/index"

const paymentTermFilters = [
  { value: 30, label: "Net 30" },
  { value: 60, label: "Net 60" },
  { value: 90, label: "Net 90" },
]

function ToolBarWithDatePicker({ tabValue }) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down("xs"))
  const tabletView = useMediaQuery(theme.breakpoints.down("sm"))

  const {
    dateInfo,
    setDateInfo,
    searchValue,
    setSearchValue,
    clientCompanyValue,
    setClientCompanyValue,
    paymentTermsFilter,
    setPaymentTermsFilter,
    unpaid,
    setUnpaid,
    clietAccepted,
    setClientAccepted,
    tableData,
  } = useContext(SuperTableContext)

  let shootData = tableData.hasOwnProperty("shootsData")
    ? tableData.shootsData
    : []

  let crewData = tableData.hasOwnProperty("crewData") ? tableData.crewData : []

  let gttData = tableData.hasOwnProperty("gttData") ? tableData.gttData : []

  const companyList = useSelector(state => state.clients.companiesList)

  /*initialize ref for search icon*/
  const inputEl = useRef(null)

  let timeout
  const debounce = (func, delay) => {
    clearTimeout(timeout)
    timeout = setTimeout(func, delay)
  }

  function handleChange(event) {
    debounce(setSearchValue(event.target.value), 600)
    //need to reset to page 1 too
  }

  const setPaymentTermFilter = val => {
    if (paymentTermsFilter.includes(val)) {
      setPaymentTermsFilter(paymentTermsFilter.filter(x => x !== val))
    } else {
      setPaymentTermsFilter([...paymentTermsFilter, val])
    }
  }

  const onIconClick = () => {
    // `current` points to the mounted text input element
    inputEl.current.focus()
  }

  /* logic to set the date range & increase/decrease date*/
  const handleChangeDate = newInfo => {
    /*dont mutate the dateInfo object*/
    let increasedStart = moment(newInfo.startDate)
      .add(newInfo.duration, 1)
      .format()
    let increasedEnd = moment(newInfo.endDate)
      .add(newInfo.duration, 1)
      .format()
    let decreasedStart = moment(newInfo.startDate)
      .subtract(newInfo.duration, 1)
      .format()
    let decreasedEnd = moment(newInfo.endDate)
      .subtract(newInfo.duration, 1)
      .format()

    if (newInfo.duration === "month") {
      if (newInfo.type === "increase") {
        increasedStart = moment(increasedStart)
          .startOf("month")
          .format()
        increasedEnd = moment(increasedEnd)
          .endOf("month")
          .format()
      }

      if (newInfo.type === "decrease") {
        decreasedStart = moment(decreasedStart)
          .startOf("month")
          .format()
        decreasedEnd = moment(decreasedEnd)
          .endOf("month")
          .format()
      }
    }

    /*increase the range and set type to a string*/
    if (newInfo.type === "increase") {
      setDateInfo({
        startDate: increasedStart,
        endDate: increasedEnd,
        duration: newInfo.duration,
        value: newInfo.value,
        type: "",
        fullFetch: false,
      })
    } else if (newInfo.type === "decrease") {
      /*decrease the range and reset type*/
      setDateInfo({
        startDate: decreasedStart,
        endDate: decreasedEnd,
        duration: newInfo.duration,
        value: newInfo.value,
        type: "",
        fullFetch: false,
      })
    } else if (newInfo.type === "changeDuration") {
      /* set start and end to the first and last day of given duration */
      // this is relative to the current date, not a date previously navigated to
      if (newInfo.duration.toLowerCase() === "all") {
        setDateInfo({
          ...dateInfo,
          duration: newInfo.duration,
          fullFetch: true,
        })
      } else {
        setDateInfo({
          startDate: moment()
            .startOf(newInfo.duration)
            .format(),
          endDate: moment()
            .endOf(newInfo.duration)
            .format(),
          duration: newInfo.duration,
          value: newInfo.value,
          type: "",
          fullFetch: false,
        })
      }
    }
  }
  const hanldeChangeUnpaid = () => {
    setUnpaid(oldState => !oldState)
  }

  const hanldeChangeAccepted = () => {
    setClientAccepted(oldState => !oldState)
  }

  const showUnpaidButton = tabValue !== "estimates" && tabValue !== "gtt"
  const showExportButton =
    tabValue === "shoots" || tabValue === "crew" || tabValue === "gtt"
      ? true
      : false
  const showAcceptedButton =
    tabValue === "invoices" || tabValue === "estimates" ? true : false

  const shootCsvFields = [
    {
      label: "Shoot Id",
      value: "shoot_id",
    },
    {
      label: "Company Name",
      value: "crewmama_or_gtt",
    },
    {
      label: "Client Name",
      value: "client_company",
    },
    {
      label: "Title",
      value: "shoot_name",
    },
    {
      label: "Shoot End Date",
      value: "shoot_end_date",
    },
    {
      label: "Paid Date",
      value: "paid_date",
    },
    {
      label: "Total",
      value: "total",
    },
    {
      label: "Expense",
      value: "expense",
    },
    {
      label: "Income",
      value: "income",
    },
    {
      label: "Outstanding Balance",
      value: "outstandingBalance",
    },
    {
      label: "Paid",
      value: "clientPay",
    },
    {
      label: "Credit",
      value: "add_amount",
    },
  ]

  const gttCsvFields = [
    {
      label: "GTT Member",
      value: "user_name",
    },
    {
      label: "Billables",
      value: "commission_billable",
    },
    {
      label: "Shoot Costs",
      value: "commission_cost",
    },
    {
      label: "Net",
      value: "commission_net",
    },
    {
      label: "Shoot ID",
      value: "shoot_id",
    },
    {
      label: "Shoot Title",
      value: "shoot_name",
    },
  ]

  const crewCsvFields = [
    {
      label: "Crew Member",
      value: "user_name",
    },
    {
      label: "Total Earned",
      value: "total_earned",
    },
    {
      label: "Outstanding Balance",
      value: "outstanding_balance",
    },
    {
      label: "Paid Out Amount",
      value: "paid_amount",
    },
    {
      label: "Shoot Id",
      value: "shoot_id",
    },
    {
      label: "Shoot Date",
      value: "shoot_date",
    },
    {
      label: "Shoot Title",
      value: "shoot_name",
    },
    {
      label: "Client Name",
      value: "client_company",
    },

    {
      label: "Client Paid",
      value: "client_paid",
    },
  ]

  return (
    <div className={classes.grow}>
      <AppBar elevation={0} position="static">
        <Toolbar classes={{ root: classes.toolbar }}>
          <div className={classes.center}>
            <SuperAdminDatePicker onChangeDate={handleChangeDate} />
            {showUnpaidButton &&
              !tabletView && (
                <Button
                  color="primary"
                  size="small"
                  classes={
                    unpaid
                      ? { root: classes.durationButton }
                      : { root: classes.durationButtonOutlined }
                  }
                  style={{ margin: "0 5px", height: "40px" }}
                  onClick={hanldeChangeUnpaid}
                >
                  Unpaid
                </Button>
              )}

            {showAcceptedButton &&
              !tabletView && (
                <Button
                  color="primary"
                  size="small"
                  classes={
                    clietAccepted
                      ? { root: classes.durationButton }
                      : { root: classes.durationButtonOutlined }
                  }
                  style={{ margin: "0 5px", height: "40px" }}
                  onClick={hanldeChangeAccepted}
                >
                  Client Accepted
                </Button>
              )}
          </div>

          {/* <div className={classes.grow} /> */}
          <div className={classes.filterSearchWrapper}>
            {/* {
              showAcceptedButton &&
              !mobileView &&
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mr={2}
              >

                <AutocompleteBilling
                  options={["Select Client Accepted","Client Accepted"]}
                 
                  placeholder={"Filter by Client Accepted"}
                  value=""
                  className={classes.autocomplete}
                  blurInputOnSelect
                  backspaceRemovesValue
                  showValue
                  isClearable
                  escapeClearsValue
                  onChange={handelInvoiceClientAcceptedChange
                  }
                />
              </Box>
            } */}

            {tabValue === "shoots" &&
              !mobileView && (
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mr={2}
                >
                  <AutocompleteBilling
                    options={companyList}
                    getOptionLabel={option => option.name}
                    placeholder={"Filter by Client Company"}
                    value={companyList.filter(
                      company =>
                        company &&
                        company.name.toLowerCase() ===
                          ((clientCompanyValue &&
                            clientCompanyValue.toLowerCase()) ||
                            null)
                    )}
                    className={classes.autocomplete}
                    blurInputOnSelect
                    backspaceRemovesValue
                    showValue
                    isClearable
                    escapeClearsValue
                    // InputClasses={{
                    //   notchedOutline: classes.companyAutocomplete,
                    // }}
                    onChange={value =>
                      setClientCompanyValue((value && value.name) || null)
                    }
                  />
                  <Box display="flex" ml={1}>
                    {paymentTermFilters.map((x, i) => (
                      <Button
                        key={i}
                        color="primary"
                        size="small"
                        classes={
                          paymentTermsFilter &&
                          paymentTermsFilter.includes(x.value)
                            ? { root: classes.durationButton }
                            : { root: classes.durationButtonOutlined }
                        }
                        style={{ margin: "0 5px", height: "40px" }}
                        onClick={() => setPaymentTermFilter(x.value)}
                      >
                        {x.label}
                      </Button>
                    ))}
                  </Box>
                  {showUnpaidButton &&
                    (tabletView && !mobileView) && (
                      <Button
                        color="primary"
                        size="small"
                        classes={
                          unpaid
                            ? { root: classes.durationButton }
                            : { root: classes.durationButtonOutlined }
                        }
                        style={{ margin: "0 5px", height: "40px" }}
                        onClick={hanldeChangeUnpaid}
                      >
                        Unpaid
                      </Button>
                    )}
                </Box>
              )}
            <Box className={classes.searchAndCopyWrapper}>
              {" "}
              <Input
                inputRef={inputEl}
                classes={{ root: classes.searchBox }}
                onChange={handleChange}
                value={searchValue}
                placeholder="Search Anything"
                disableUnderline
                startAdornment={<SearchIcon onClick={onIconClick} />}
                className={classes.SearchIcon}
              />
              {showExportButton && (
                <ExportCsvButton
                  data={
                    tabValue === "shoots"
                      ? shootData
                      : tabValue === "crew"
                        ? crewData
                        : tabValue === "gtt"
                          ? gttData
                          : []
                  }
                  fields={
                    tabValue === "shoots"
                      ? shootCsvFields
                      : tabValue === "crew"
                        ? crewCsvFields
                        : tabValue === "gtt"
                          ? gttCsvFields
                          : []
                  }
                  tab={tabValue}
                />
              )}
              {/* <IconButton
                onClick={copyData}
                size="small"
                style={{
                  padding: "6px",
                }}
              >
                <FileCopyIcon fontSize="small" style={{ fill: "black" }} />
              </IconButton> */}
            </Box>
          </div>
        </Toolbar>
      </AppBar>
    </div>
  )
}
ToolBarWithDatePicker.propTypes = {
  tabValue: PropTypes.string,
}
export default ToolBarWithDatePicker
